import React, {
  useEffect, useCallback, useState, useRef,
} from 'react';
import { toast } from 'react-toastify';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Tooltip } from '@mui/material';
import RightSection from '../components/Dashboard/MenuSection/RightSection';
import MyNetworkMap from '../components/MyNetworkMapView';
import AddPortService from '../services/addPort.service';
import MyNNetworkService from '../services/myNetwork.service';
import { ErrorMessages as Errors } from '../utils/apiConstants';
import ErrorInfoIcon from '../assets/images/error-info-icon.svg';
import FiltersDrawer from '../components/MyNetworkMapView/FiltersDrawer';
import DashboardService from '../services/dashboard.service';
import { useIsMounted } from "../utils/customHooks";

const listFilterOptions = [{ label: 'Both', value: 'both' }, { label: 'Contract', value: 'onContract' }, { label: 'On Demand', value: 'onDemand' }];

const MyNetwork: React.FC = () => {
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [selectedNes, setSelectedNes] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [bandwidthPorts, setBandwidthPorts] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [mapFilters, setMapFilters] = useState<{
    available: boolean, consumed: boolean, sold: boolean,
    contract: boolean, demand: boolean, other: boolean,
  }>({
    available: true,
    consumed: true,
    sold: true,
    contract: false,
    demand: false,
    other: false,
  });
  const [listBandwidths, setListBandwidths] = useState<{
    available: any[], consumed: any[], sold: any[], contract: any[], demand: any[], other: any[]
  }>({
    available: [],
    consumed: [],
    sold: [],
    contract: [],
    demand: [],
    other: [],
  });
  const [bandwidthPointIds, setBandwidthPointIds] = useState<{
    neId: string, feId: string
  }>({
    neId: '', feId: '',
  });
  const [search, setSearch] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  const isComponentMounted = useRef(true);
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  const isMounted = useIsMounted();

  const handleBandwidthClick = useCallback((neId: string, feId: string) => {
    setLoading(true);
    setBandwidthPointIds({
      neId,
      feId,
    });
    const filters: string[] = [];

    Object.keys(mapFilters).forEach((filter: any) => {
      filters.push(filter.toString());
    });
    const neIds = neId === '' || feId === '' ? [] : [neId, feId];
    const payload = {
      filters,
      searchText,
      neIds,
    };
    const tsError = 'ts-error';
    MyNNetworkService.getMyNetworkBandwidthsList(payload)
      .then((response: any) => {
        if (!isComponentMounted.current) return;
        setLoading(false);

        let totalCount = 0;
        if (response?.data?.availableResult && response?.data?.consumedResult && response?.data?.soldResult) {
          totalCount += response?.data?.availableResult.length ?? 0;
          totalCount += response?.data?.consumedResult.length ?? 0;
          totalCount += response?.data?.soldResult.length ?? 0;
          totalCount += response?.data?.contractResult.length ?? 0;
          totalCount += response?.data?.otherResult.length ?? 0;
          totalCount += response?.data?.demandResult.length ?? 0;
        } else {
          throw tsError;
        }

        setListBandwidths({
          available: response?.data?.availableResult,
          consumed: response?.data?.consumedResult,
          sold: response?.data?.soldResult,
          demand: response?.data?.demandResult,
          contract: response?.data?.contractResult,
          other: response?.data?.otherResult,
        });
        setCount(totalCount);
      })
      .catch((e) => {
        if (!isComponentMounted.current) return;
        if (e !== tsError) {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
        setLoading(false);
        setCount(0);
        setListBandwidths({
          available: [],
          consumed: [],
          sold: [],
          contract: [],
          demand: [],
          other: [],
        });
      });
  }, [mapFilters, searchText, setLoading, setCount, setListBandwidths]);

  const fetchBandwidthForMyNetwork = useCallback(() => {
    const filters: string[] = [];

    Object.keys(mapFilters).forEach((filter: any) => {
      // @ts-ignore
      if (mapFilters[filter]) {
        filters.push(filter.toString());
      }
    });

    const payload = {
      filters,
    };

    MyNNetworkService.getMyNetworkBandwidths(payload)
      .then((response: any) => {
        if (!isComponentMounted.current) return;
        setBandwidthPorts(response?.data ?? []);
      })
      .catch(() => {
        if (!isComponentMounted.current) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setBandwidthPorts([]);
      });
  }, [setBandwidthPorts, mapFilters]);

  const fetchNetworkElements = useCallback(() => {
    AddPortService.getNetworkElements()
      .then((response: any) => {
        if (!isComponentMounted.current) return;

        setNetworkElements(response.data || []);
        fetchBandwidthForMyNetwork();
      })
      .catch(() => {
        if (!isComponentMounted.current) return;

        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setNetworkElements([]);
      });
  }, [setNetworkElements, fetchBandwidthForMyNetwork]);

  useEffect(() => {
    fetchNetworkElements();
  }, [fetchNetworkElements]);

  useEffect(() => {
    handleBandwidthClick(bandwidthPointIds.neId, bandwidthPointIds.feId);
  }, [handleBandwidthClick, searchText, bandwidthPointIds.neId, bandwidthPointIds.feId]);

  const handleListSearchChanges = useCallback((e: any) => {
    setSearch(e.target.value.trim());
    if (e.target.value.trim() === '') {
      setSearchText('');
      handleBandwidthClick(bandwidthPointIds.neId, bandwidthPointIds.feId);
    }
  }, [setSearch, handleBandwidthClick, setSearchText, bandwidthPointIds.neId, bandwidthPointIds.feId]);

  const handleSearchClicked = useCallback(() => {
    setSearchText(search);
  }, [setSearchText, search]);
  
  const fetchNodeIds = useCallback(() => {
    DashboardService.getSelectedNodes()
      .then((response: any) => {
        if (!isMounted) return; // ✅ Works if isMounted is a boolean
        setSelectedNes(response.data.selected_node_ids || []);
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { 
          icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> 
        });
        setSelectedNes([]);
      });
  }, [setSelectedNes, isMounted]);
  
  useEffect(() => {
    fetchNodeIds();
  }, [fetchNodeIds]);

  return (
    <div className="my-network-map-root canvas-bg">
      <div className="right-section">
        <RightSection />
      </div>
      <FiltersDrawer onOpen={() => setFilterDrawerOpen(true)} onClose={() => setFilterDrawerOpen(false)} open={filterDrawerOpen} mapFilters={mapFilters} setMapFilters={setMapFilters} />
      <div className="bottom">
        <MyNetworkMap
          onSearchChange={handleListSearchChanges}
          onSearchClicked={handleSearchClicked}
          listBandwidths={listBandwidths}
          searchText={search}
          loading={loading}
          count={count}
          networkElements={networkElements}
          selectedNes={selectedNes}
          bandwidthPorts={bandwidthPorts}
          onNEClick={() => {}}
          onBandwidthClick={handleBandwidthClick}
        />
      </div>
    </div>
  );
};

export default MyNetwork;
